import { useMemo } from "react";
import clsx from "clsx";

import { QuoteType } from "../../../models/Quote";
import { BreakdownRow, BreakdownSide, buildBreakdown, formatPrice } from "./calculationBreakdown";

const signed = (amount: number) => `${amount < 0 ? "−" : "+"} ${formatPrice(Math.abs(amount))}`;

const Row = ({ row }: { row: BreakdownRow }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 text-sm">
    <div className="min-w-0">
      <div className="text-gray-900 break-words">{row.name}</div>
      <div className="text-xs text-gray-500 break-words">{row.detail}</div>
    </div>
    <div
      className={clsx(
        "shrink-0 tabular-nums",
        row.amount == null || row.amount === 0
          ? "text-gray-400"
          : row.amount < 0
            ? "text-red-600"
            : "text-green-700"
      )}
    >
      {row.amount == null ? "—" : signed(row.amount)}
    </div>
  </div>
);

/**
 * One side (collection or drop off) of a single customer's pricing run. Every price type is shown
 * so it's clear why the winning one won; the winner is highlighted and carries the final price.
 */
export default function BreakdownView({
  calculationJson,
  side,
  winningType,
  total,
}: {
  calculationJson?: string | null;
  side: BreakdownSide;
  winningType: QuoteType;
  total: number;
}) {
  const breakdown = useMemo(() => buildBreakdown(calculationJson, side), [calculationJson, side]);

  if (!breakdown) {
    return <p className="text-sm text-gray-500">No calculation data recorded.</p>;
  }

  const hasPrice = total >= 0;
  const showDropOffFee = side === "dropOff" && breakdown.dropOffFee > 0;

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        {breakdown.sections.map((section) => {
          const isWinner = hasPrice && section.type === winningType;

          return (
            <div
              key={section.type}
              className={clsx(
                "rounded-md border p-3",
                isWinner ? "border-green-500 bg-green-50/60" : "border-gray-200 bg-white"
              )}
            >
              <div className="mb-1 flex items-center justify-between">
                <h4 className="text-sm font-semibold text-gray-900">{section.title}</h4>
                {isWinner && (
                  <span className="rounded-full bg-green-600 px-2 py-0.5 text-[11px] font-medium text-white">
                    Used
                  </span>
                )}
              </div>

              {section.rows.length === 0 ? (
                <p className="py-1.5 text-xs text-gray-400">Not calculated for this customer</p>
              ) : (
                <div className="divide-y divide-gray-100">
                  {section.rows.map((row, i) => (
                    <Row key={i} row={row} />
                  ))}
                </div>
              )}

              {isWinner && (
                <div className="mt-2 border-t border-green-200 pt-2 text-sm">
                  {showDropOffFee && (
                    <div className="flex justify-between text-gray-700">
                      <span>Drop off fee</span>
                      <span className="tabular-nums text-green-700">{signed(breakdown.dropOffFee)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold text-gray-900">
                    <span>Total</span>
                    <span className="tabular-nums">{formatPrice(total)}</span>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {breakdown.extras.length > 0 && (
        <div className="rounded-md border border-gray-200 p-3">
          <h4 className="mb-1 text-sm font-semibold text-gray-900">Added on acceptance</h4>
          <div className="divide-y divide-gray-100">
            {breakdown.extras.map((row, i) => (
              <Row key={i} row={row} />
            ))}
          </div>
        </div>
      )}

      {breakdown.skippedRules.length > 0 && (
        <details className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm">
          <summary className="cursor-pointer font-medium text-amber-800">
            {breakdown.skippedRules.length} specific rule{breakdown.skippedRules.length === 1 ? "" : "s"} nearly applied
          </summary>
          <ul className="mt-2 list-disc space-y-1 pl-5 text-xs text-amber-900">
            {breakdown.skippedRules.map((rule, i) => (
              <li key={i}>{rule}</li>
            ))}
          </ul>
        </details>
      )}
    </div>
  );
}
